/**
 * BillingWorkspace — the AR Billing tools as an in-app WORKSPACE (like the outline), not a modal
 * and not a separate route. Docks into the content area beside SiteNav (tray-aware via the
 * `.aj-billing-ws` rule in outline.css) and shows when active; the nav stays usable and "Projects"
 * returns to the outline. Hosts the two ported imperative tools (billing/readinessTool.js,
 * runTool.js) via their mount(root) → teardown contract.
 *
 * Open: `open-billing` CustomEvent (detail { tool:'readiness'|'run', runId? }) — dispatched by the
 * SiteNav Billing item and by the tools' cross-navigation. Close: `close-billing` (SiteNav Projects).
 *
 * State (active tool + runId) persists to localStorage so a page refresh restores the billing
 * workspace where you left it instead of dropping back to the outline.
 */

import { useEffect, useRef, useState } from 'react'
import { WorkspaceHeader, workspaceTabsWrapStyle, workspaceTabStyle } from './WorkspaceChrome.jsx'
import FinancesTabs from './FinancesTabs.jsx'
import { hasModule, isSeatLoaded } from '../services/tenancy.js'
import { ctx } from '../outline/ctx.js'
import { isEmbedded } from '../outline/state.js'
import { mountReadiness } from '../billing/readinessTool.js'
import { mountRun } from '../billing/runTool.js'

const STORE_KEY = 'aj:billing:ws'
// Per-tab live state (sessionStorage) + shared last-session snapshot (localStorage), so opening the
// Billing workspace in one tab doesn't force a second tab into it — while a fresh tab / return visit
// still restores it. Read sessionStorage first, fall back to the shared snapshot.
//
// `runId` is deliberately NOT restored from storage (only from an explicit `wsRunId` already in the
// CURRENT URL, e.g. a real deep link). It's a one-shot "jump to this specific run" hint — set when
// Readiness's "view this run" link fires `open-billing` with a runId — that runTool.js itself only
// ever consumes once, on its true initial mount (see its own `state.urlIdConsumed` comment). Storing
// it as durable workspace state used to mean: click into an old month's run from Readiness, then any
// later cold reload of the Billing Run tab silently re-pinned to that same stale run instead of the
// current month's — showing 0 (or wrong) line items until the user navigated months or bounced
// through Readiness (both of which happened to clear/bypass it). Matt: "When I refresh the page on
// the billing run, the line items don't load unless I..." — this was that bug.
function readStored() {
  try {
    const params = new URLSearchParams(window.location.search)
    const ws = params.get('ws')
    if (ws === 'billing') return { active: true, tool: params.get('wsTool') || 'readiness', runId: params.get('wsRunId') || null }
    if (ws !== null) return null   // explicit workspace in URL — don't fall back to storage
    // A peek/split iframe must never auto-restore a workspace from storage shared with the
    // parent tab — see ScheduleIntelWorkspace.jsx's own comment on this same check.
    if (isEmbedded()) return null
    const t = sessionStorage.getItem(STORE_KEY)
    const p = t ? JSON.parse(t) : JSON.parse(localStorage.getItem(STORE_KEY) || 'null')
    return p ? { active: p.active, tool: p.tool } : null
  } catch (e) { return null }
}

export default function BillingWorkspace() {
  const stored = readStored()
  const [active, setActive] = useState(!!(stored && stored.active))
  const [tool, setTool] = useState(stored && stored.tool === 'run' ? 'run' : 'readiness')   // 'readiness' | 'run'
  const [runId, setRunId] = useState((stored && stored.runId) || null)
  const [seatReady, setSeatReady] = useState(isSeatLoaded())
  const contentRef = useRef(null)

  // open/close via events
  useEffect(() => {
    function onOpen(e) {
      const d = (e && e.detail) || {}
      setTool(d.tool === 'run' ? 'run' : 'readiness')
      setRunId(d.runId || null)
      setActive(true)
    }
    function onClose() { setActive(false) }
    window.addEventListener('open-billing', onOpen)
    window.addEventListener('close-billing', onClose)
    return () => {
      window.removeEventListener('open-billing', onOpen)
      window.removeEventListener('close-billing', onClose)
    }
  }, [])

  // Persist the workspace state so a refresh restores it. sessionStorage = this tab's live state
  // (per-tab); localStorage = shared last-session snapshot (fresh tab / return visit).
  // runId is intentionally excluded — see readStored()'s comment above.
  useEffect(() => {
    try {
      if (active) {
        const json = JSON.stringify({ active: true, tool })
        sessionStorage.setItem(STORE_KEY, json)
        localStorage.setItem(STORE_KEY, json)
      } else {
        sessionStorage.removeItem(STORE_KEY)
        // Leave the localStorage snapshot intact only if it reflects a different tab's open billing;
        // clear it here so closing billing in the active tab doesn't strand the shared snapshot.
        localStorage.removeItem(STORE_KEY)
      }
    } catch (e) { /* ignore */ }
  }, [active, tool])

  useEffect(() => {
    const url = new URL(window.location)
    if (active) {
      url.searchParams.set('ws', 'billing')
      url.searchParams.set('wsTool', tool)
      url.searchParams.delete('wsRunId')   // one-shot deep-link hint only — never a durable URL param
      document.title = 'Finances - Billing'
    } else if (url.searchParams.get('ws') === 'billing') {
      url.searchParams.delete('ws')
      url.searchParams.delete('wsTool')
      url.searchParams.delete('wsRunId')
    }
    window.history.replaceState(null, '', url)
  }, [active, tool])

  useEffect(() => {
    if (!active) return
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [active])

  // Seat data loads async at startup; on a refresh-restore the tool can't mount until it's ready.
  useEffect(() => {
    if (seatReady) return
    const id = setInterval(() => { if (isSeatLoaded()) { setSeatReady(true); clearInterval(id) } }, 100)
    return () => clearInterval(id)
  }, [seatReady])

  // Don't strand a non-billing user in an empty restored panel.
  useEffect(() => {
    if (active && seatReady && !hasModule('billing-admin')) setActive(false)
  }, [active, seatReady])

  // mount/teardown the active tool into the content container
  useEffect(() => {
    if (!active) return
    if (!seatReady || !hasModule('billing-admin')) return
    const el = contentRef.current
    if (!el) return
    // the ported run tool stamps "Last Update By" / "PM Setup Completed By" from this global
    try {
      window.logged_in_user = {
        airtable_record_id: (ctx.currentMember && ctx.currentMember.id) || '',
        name: (ctx.currentMember && ctx.currentMember.fields && ctx.currentMember.fields['Member Name']) || '',
      }
    } catch (e) { /* non-fatal */ }
    el.innerHTML = ''
    let teardown
    try {
      teardown = tool === 'run' ? mountRun(el, { runId }) : mountReadiness(el)
    } catch (e) { /* surface nothing — tool renders its own errors */ }
    return () => {
      try { teardown && teardown() } catch (e) { /* ignore */ }
      if (el) el.innerHTML = ''
    }
  }, [active, tool, runId, seatReady])

  if (!active) return null

  const restricted = isSeatLoaded() && !hasModule('billing-admin')

  return (
    <div className="aj-billing-ws" style={S.panel}>
      <WorkspaceHeader
        title="Finances"
        tabs={!restricted && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <FinancesTabs current="billing" />
          <div style={workspaceTabsWrapStyle}>
            <button style={workspaceTabStyle(tool === 'readiness')} onClick={() => { setRunId(null); setTool('readiness') }}>Readiness</button>
            <button style={workspaceTabStyle(tool === 'run')} onClick={() => setTool('run')}>Billing Run</button>
          </div>
          </div>
        )}
      />
      {restricted ? (
        <div style={S.restricted}>You don't have a billing seat. Contact your administrator.</div>
      ) : (
        <div ref={contentRef} style={S.content} />
      )}
    </div>
  )
}

const S = {
  panel: { display: 'flex', flexDirection: 'column' },   // position/size/bg from .aj-billing-ws in outline.css
  content: { flex: 1, overflow: 'auto' },
  restricted: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-mid)' },
}
